import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import "./RootLayoutV2.css";
import { LegalDisclaimer } from "../global/LegalDisclaimer";
import MyAccount from "@vds/icons/my-account";
import { Layout } from "antd";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import ErrorBoundaryWrapper from "../global/components/ErrorHandling/ErrorBoundaryWrapper";
import NavMenu from "../global/components/NavMenu";
import { pageTypes } from "../global/constants/pageTypes";
import { useWebsocket } from "../global/hooks/useWebsocket";
import { useWindowSize } from "../global/hooks/viewportHooks";
import { getLeftNavSelection } from "../global/managers/NavServiceManager";
import { flattenSearchResults } from "../pages/HistoricalSearch/ForensicSearchUtil";
import { getLicenseOveruseState, getSessionStorageData } from '../global/utility/Utils';
import { safeDecodeURIComponent } from '../global/utility/devicesUtils';
import { menuLists } from "../model/appmenus";
import {
  clearErrorMessage,
  eventPageNumber,
  eventSettings,
  fetchCriticalUnresolvedEventsRequest,
  showNotificationBanner,
  showCalibrationBanner,
  updateCCDStatus,
  getForensicSearchStatusResponse,
  updateForensicSearchImagesResponse,
} from "../store/actions";
import NotificationBanner from "./NotificationBanner";
import { BANNER_TIMEOUT, USER_ROLE } from "../global/constants/constants";
import CalibrationBanner from "./CalibrationBanner";
import { STORAGE } from "../global/constants/storageConstants";
import CortexBar from "../global/chatbot/CortexBar";
import CortexGlobalModal from "../global/chatbot/CortexGlobalModal";
import { ROUTES, SAFE_REDIRECT_ROUTES } from "../global/constants/routesConstants";

const RootLayoutV2 = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userRole = useSelector((state) => state.userRoleReducer.userRole);
  const isNluEnabled = useSelector((state) => state?.chatbot?.isNluEnabled);
  const companionDeviceList = useSelector((state) => state?.deviceReducer?.companionDeviceList);
  const [ccdDeviceName, setCcdDeviceName] = useState("");
  const [isNavOpen, setIsNavOpen] = useState(false);
  const getCompanionDeviceName = (id) => {
    if (companionDeviceList && companionDeviceList.length > 0) {
      const device = companionDeviceList.find((item) => item.id === id);
      return device ? device.name : null;
    }
  };

  const { activeSession } = useSelector (
    (state) => state.sessionReducer.activeSession,
    shallowEqual
  );
  const bannerInfo = useSelector(
    (state) => state.notificationBanner.bannerInfo
  );
  const calibrationBannerInfo = useSelector(
    (state) => state.calibrationBanner.bannerInfo
  );

  const accountId = activeSession?.accountId;
  // Redis mode doesn't store sessionToken, so send the Redis session id instead
  const sessionToken = activeSession?.isRedisMode
    ? activeSession?.sessionState
    : activeSession?.sessionToken;

  // accountDetails is always in sessionStorage after login
  // Removed localStorage fallback to avoid storing sensitive PII
  const userDetails = getSessionStorageData(STORAGE.ACCOUNT_DETAILS);
  const firstName = userDetails?.firstname || "";
  const lastName = userDetails?.lastname || "";
  const fullName = `${firstName} ${lastName}`;
  const { Content } = Layout;
  const userId = userDetails?.id || "";
  const [showNotification, setShowNotification] = useState(false);
  const [showCCDNotification, setShowCCDNotification] = useState(false);
  const message = useSelector((state) => state.errorReducer.message);
  const stickyBannerContainerStyle = {
    position: 'sticky',
    top: showNotification ? 100 : 0,
    zIndex: 99,
  };
  const showLegalDisclaimer = window.REACT_APP_LEGAL_DISCLAIMER === "true";
  const [ready, val, setVal, ccdValue, setCcdValue, forensicSearchValue, setForensicSearchValue, imageUploadValue, setImageUploadValue] = useWebsocket(
    `${window.REACT_APP_USE_REDIS_SESSION === 'true' ? window.REACT_APP_SESSION_MANAGER_WEBSOCKET_HOST_REDIS : window.REACT_APP_SESSION_MANAGER_WEBSOCKET_HOST}/`,
    accountId,
    sessionToken,
    userId,
    activeSession?.isRedisMode
  );

  const { width: viewportWidth } = useWindowSize();
  const isMobileView = (viewportWidth ?? 1400) < 1400;
  const sidebarIconSize = viewportWidth < 768 ? "small" : "medium";

  // Close nav when viewport grows past the mobile breakpoint
  useEffect(() => {
    if (!isMobileView) setIsNavOpen(false);
  }, [isMobileView]);

  const licenses = useSelector((state) => state.licenseReducer.licenses);
  const { siteTagList } = useSelector((state) => state?.sitesReducer);
  const sites = siteTagList?.sites;
  const rurl = sessionStorage.getItem(STORAGE.RURL);
  const dispatch = useDispatch();
  const isFetchCallInProgress = useRef(false);
  // NPDIOTS-9725: WS event arriving while a fetch is already in-flight would be silently
  // dropped by the isFetchCallInProgress gate. These two refs coalesce any such dropped
  // trigger into exactly one trailing refetch once the in-flight one settles.
  const pendingRefetchRef = useRef(false);
  const pendingRefetchSitesRef = useRef(null);
  const isCriticalEventsFetching = useSelector(
    (state) => state.eventReducer.isCriticalEventsFetching
  );

  const isMenuDisabled = useMemo(() => {
    if (pathname !== "/licenses" && pathname !== "/licensedetails") {
      return false;
    }
    return licenses.some(
      (lic) => getLicenseOveruseState(lic.overusestartedon) === 'expired'
    );
  }, [licenses, pathname]);

  useEffect(() => {
    if (rurl && rurl !== "null" && rurl.trim() !== "" && rurl !== ROUTES.DASHBOARD && rurl !== ROUTES.LOGIN) {
      try {
        const url = new URL(rurl, window.location.origin);
        const pathOnly = url.pathname;
        // Validate path is in whitelist and ensure it's from same origin
        const safeRoute = SAFE_REDIRECT_ROUTES.find((route) => route === pathOnly);

        //if roi or polygon page, re-direct to dashboard
        if (safeRoute === ROUTES.ROI || safeRoute === ROUTES.POLYGON) {
          return navigate(ROUTES.DASHBOARD);
        }

        if (safeRoute === ROUTES.SEARCH_RESULT) {
          return navigate(ROUTES.SEARCH);
        }
        
        if (safeRoute === ROUTES.APPEARANCE_DEPLOYMENTS) {
          return navigate(ROUTES.ENROLLED_APPEARANCE);
        }

        if (safeRoute && url.origin === window.location.origin) {
          const decodedSearch = safeDecodeURIComponent(url.search);
          const searchParams = url.search && !/[<>'"]/g.test(decodedSearch) ? url.search : '';
          navigate(safeRoute + searchParams);
        }
        sessionStorage.removeItem(STORAGE.RURL);
      } catch (error) {
        // Invalid URL format - ignore redirect
        sessionStorage.removeItem(STORAGE.RURL);
      }
    }
  }, [rurl]);

  useEffect(() => {
    if (!isCriticalEventsFetching) {
      isFetchCallInProgress.current = false;
      // Fire exactly one trailing fetch for whatever WS event(s) got gate-dropped
      // while the previous fetch was in-flight (NPDIOTS-9725).
      if (pendingRefetchRef.current) {
        pendingRefetchRef.current = false;
        isFetchCallInProgress.current = true;
        dispatch(fetchCriticalUnresolvedEventsRequest(pendingRefetchSitesRef.current, true));
      }
    }
  }, [isCriticalEventsFetching]);

  useEffect(() => {
    if (ready && val?.id) {
      setShowNotification(true);
      if (
        getLeftNavSelection(pathname).toLowerCase() ===
        pageTypes.DASHBOARD.toLowerCase()
      ) {
        let shouldUpdateSilently = false;
        if (sites && sites.length) {
          try {
            if (Object.keys(val).length) {
              shouldUpdateSilently = true;
            }
          } catch (error) {
            console.error("Invalid JSON data from websocket:", error);
          }

          if (!isFetchCallInProgress.current) {
            isFetchCallInProgress.current = true;
            dispatch(fetchCriticalUnresolvedEventsRequest(sites, shouldUpdateSilently));
          } else {
            // Gated — don't drop this trigger, coalesce it into one trailing fetch (NPDIOTS-9725)
            pendingRefetchRef.current = true;
            pendingRefetchSitesRef.current = sites;
          }
        }
      }
    }
  }, [ready, val]);

   useEffect(() => {
    if (ready && ccdValue && (ccdValue.id || ccdValue.resource?.deviceid)) {
      const id = ccdValue.id || ccdValue.resource.deviceid || "";
      const fields = ccdValue.resource?.fields || {};
      const deviceName = getCompanionDeviceName(id);
      setCcdDeviceName(deviceName);
      dispatch(updateCCDStatus({ id, fields }));

      if (
        getLeftNavSelection(pathname).toLowerCase() ===
        pageTypes.DEVICES.toLowerCase()
      ) {
        setShowCCDNotification(true);
        setTimeout(() => {
          setShowCCDNotification(false);
        }, BANNER_TIMEOUT);
      }
    }
  }, [ready, ccdValue]);

  useEffect(() => {
    if (ready && forensicSearchValue?.payload?.id) {
      console.log(`[RootLayoutV2] search completed event received for search ${forensicSearchValue.payload.id}`);
      // Kafka completion event carries the same raw grouped `results` shape as the REST
      // response (ccdId -> searchIndexes -> entries) - flatten it the same way the REST saga
      // does via flattenSearchResults, so results merge in ready-to-consume by
      // getImagesFromImageIndex without waiting on a REST refetch.
      const searchStatusFields = flattenSearchResults(forensicSearchValue.payload);
      dispatch(
        getForensicSearchStatusResponse({
          statusCode: 200,
          statusText: "",
          responseData: searchStatusFields,
        })
      );
      setForensicSearchValue(null);
    }
  }, [ready, forensicSearchValue]);

  useEffect(() => {
    if (ready && Array.isArray(imageUploadValue) && imageUploadValue.length > 0) {
      const imageUploadEvent = imageUploadValue[0];
      const { batchSearchId, id, data } = imageUploadEvent.payload;
      const entryIds = (data || []).map((d) => d.entryId).join(", ");
      console.log(`[RootLayoutV2] image uploaded event received for search ${id ?? batchSearchId}, ${data?.length ?? 0} image(s) [${entryIds}], queue: ${imageUploadValue.length - 1} remaining`);
      // Pending entries don't have imageId yet (assigned only once upload completes) - match
      // on entryId, which mirrors the entry's imageIndex sent in the original trigger request
      // (getImageUrlFromForensicSearchImageIndex's searchIndex.entryids). Matching by imageId
      // (both undefined) would silently no-op for exactly the entries this event is meant to fix.
      const images = (data || []).map((d) => ({
        entryId: d.entryId,
        imageId: d.imageId,
        s3url: d.s3url,
        error: d.error,
        status: d.error ? "failed" : "uploaded",
      }));
      // `id` matches the search entity id used everywhere else (SEARCH_COMPLETED merge,
      // REST resourceidentifier); `batchSearchId` is a separate upload-batch id and does
      // NOT match store search.id — must not be preferred here.
      dispatch(
        updateForensicSearchImagesResponse({ searchId: id ?? batchSearchId, images })
      );
      setImageUploadValue((prev) => prev.slice(1));
    }
  }, [ready, imageUploadValue]);

  useEffect(() => {
    if (
      pathname !== "/events" &&
      pathname !== "/eventsInfo" &&
      !pathname.includes("KNOWI")
    ) {
      if (!pathname.includes("KNOWI")) {
        const payload = {
          Events: [],
          Location: { siteid: "", floorid: "", areaid: "" },
          Companion: [],
          Severity: [],
          Status: [],
          startTime: "",
          endTime: "",
        };
        dispatch(eventSettings(payload));
        dispatch(eventPageNumber(1));
      }
    }
  }, [pathname]);

  // Auto-dismiss notifications based on autoDismiss property
  // Set autoDismiss: false in notification config to make it persistent
  useEffect(() => {
    let timer;
    if (bannerInfo?.showNotification && bannerInfo.isDismissible !== false && bannerInfo.autoDismiss !== false) {
      timer = setTimeout(() => {
        dispatch(showNotificationBanner({ showNotification: false }));
      }, BANNER_TIMEOUT);
    }
    return () => clearTimeout(timer);
  }, [bannerInfo, dispatch]);

  useEffect(() => {
    let timer;
    if (calibrationBannerInfo?.showNotification && calibrationBannerInfo.isDismissible !== false && calibrationBannerInfo.autoDismiss !== false) {
      timer = setTimeout(() => {
        dispatch(showCalibrationBanner({ showNotification: false }));
      }, BANNER_TIMEOUT);
    }
    return () => clearTimeout(timer);
  }, [calibrationBannerInfo, dispatch]);

  useEffect(() => {
    if (!showNotification) {
      setVal(null);
    }
  }, [showNotification]);

  useEffect(() => {
    if (!showCCDNotification) {
      setCcdValue(null);
      setCcdDeviceName("");
    }
  }, [showCCDNotification]);

  useEffect(() => {
    reset();
    // Clear persistent notification banners (aggregated or non-auto-dismiss) when navigating to a new page
    if (bannerInfo?.showNotification && (bannerInfo?.isAggregated || bannerInfo?.autoDismiss === false)) {
      dispatch(showNotificationBanner({ showNotification: false }));
    }
    if (calibrationBannerInfo?.showNotification && (calibrationBannerInfo?.isAggregated || calibrationBannerInfo?.autoDismiss === false)) {
      dispatch(showCalibrationBanner({ showNotification: false }));
    }
  }, [pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileView && isNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileView, isNavOpen]);

  // Close mobile menu when navigating to a new page
  useEffect(() => {
    if (isNavOpen) {
      setIsNavOpen(false);
    }
  }, [pathname]);

  const handleUserDetailsClick = () => {
    navigate("/userdetails", {
      state: { userDetails },
    });
  };

  if (!activeSession) {
    return navigate("/login");
  }

  const reset = () => {
    if (message) {
      dispatch(clearErrorMessage());
    }
  };

  return (
    <>
      {/* wherever banner needed pls move this to redux, pls refer user management flow */}
      {showNotification && (
        <div className="top-notification-banner">
          <NotificationBanner
            notificationInfo={val}
            setShowNotification={setShowNotification}
          />
        </div>
      )}
      <div style={{ height: "100%", width: "100%" }}>
        {/* <Header /> */}
        <div
          className={`sasp_d-flex ${showNotification ? "p-90" : "p-0"} ${pathname.includes(ROUTES.ASSISTANT) ? "root-layout--assistant" : "root-layout--default"}`}
        >
          <div className={`left-nav${isNavOpen ? ' nav-open' : ''}`}>
            {/* Hamburger toggle — tablet & below only; sits at the top of the icon strip */}
            {isMobileView && (
              <button
                className="hamburger-btn sidebar-hamburger"
                onClick={() => setIsNavOpen(prev => !prev)}
                aria-label="Toggle navigation"
              >
                {isNavOpen ? '✕' : '☰'}
              </button>
            )}
            <div className="left-nav-header">
              <h5 className="left-nav-brand-title">
                {isMenuDisabled ? (
                  <span className="tabTitles" style={{ cursor: 'auto', color: '#a8a8a8' }}>
                    <strong>5G Video Insights</strong>
                  </span>
                ) : (
                  <Link
                  data-testid="rootLayoutV2_dashboardLink"
                  to="/dashboard"
                  className="tabTitles"
                >
                    <strong>5G Video Insights</strong>
                  </Link>
                )}
              </h5>
            </div>
            <div>
              <button
                onClick={() => handleUserDetailsClick()}
                className="tabTitles left-nav-user-btn"
                disabled={isMenuDisabled}
              >
                <span style={isMobileView && !isNavOpen ? {} : { marginRight: "8px", marginLeft: "5px" }}>
                  <MyAccount color={"#FFFFFF"} size={isMobileView && !isNavOpen ? "medium" : "XLarge"} />
                </span>
                <span
                  className="left-nav-username"
                  title={fullName}
                >
                  {fullName}
                </span>
              </button>
            </div>

            <hr className="left-nav-divider" />

            <div>
              <NavMenu
                menuItems={
                  (userRole === USER_ROLE.USER ? menuLists["User"] : menuLists["Manage"])
                    .filter(item => isNluEnabled || item.name !== "Assistant")
                }
                menuName="Manage"
                disabled={isMenuDisabled}
                collapsed={isMobileView && !isNavOpen}
                iconSize={isMobileView ? sidebarIconSize : "medium"}
              />
            </div>
            <div>
              <p
                style={{
                  color: "#A7A7A7",
                  paddingLeft: "15px",
                  paddingTop: "300px",
                }}
              >
                {/* <Icon name="connect" color="#00B845" size="medium" /> */}
                {/* &nbsp; Network connected */}
              </p>
              <p style={{ color: "#A7A7A7", paddingLeft: "15px" }}>
                {/* <Icon name="sync" color="#A7A7A7" size="medium" /> */}
                {/* &nbsp; Last refreshed */}
              </p>
            </div>
          </div>
          <Layout className="main-content">
            {isNavOpen && (
              <div className="nav-backdrop" onClick={() => setIsNavOpen(false)} />
            )}            
            <div style={stickyBannerContainerStyle}>
              {showCCDNotification && (
                <div className="top-notification-banner">
                  <NotificationBanner
                    notificationInfo={{
                      title: "CCD Notification",
                      data:{
                        ccdName: ccdDeviceName || (ccdValue.id || ccdValue.resource?.deviceid || ""),
                        ...ccdValue.resource?.fields
                      },
                      type: "CCD firmware update",
                      priority: "info",
                    }}
                    setShowNotification={setShowCCDNotification}
                  />
                </div>
              )}
            </div>
            <Content>
                {/* {message != null && (
                <Notification
                  type="error"
                  title="Forbidden Error"
                  subtitle={message}
                  fullBleed={true}
                  inline={false}
                  disableFocus={true}
                  hideCloseButton={false}
                  onCloseButtonClick={() => reset()}
                />
              )} */}
                <div style={stickyBannerContainerStyle}>
                  <CortexBar />
                  <CalibrationBanner />
                  {bannerInfo.showNotification && (
                    <NotificationBanner
                      notificationInfo={bannerInfo}
                      setShowNotification={() => dispatch(showNotificationBanner({ showNotification: false }))}
                    />
                  )}
                </div>
                <div
                  className={pathname.includes(ROUTES.ASSISTANT)
                    ? `root-content--assistant${showNotification ? " root-content--notification-shown" : ""}`
                    : "root-content"
                  }
                >
                  <ErrorBoundaryWrapper>
                    <Outlet />
                  </ErrorBoundaryWrapper>
                </div>
              {showLegalDisclaimer && LegalDisclaimer()}
            </Content>
          </Layout>
        </div>
      </div>
      <CortexGlobalModal />
    </>
  );
};

export default RootLayoutV2;
